import {
  ColorPicker,
  RoundColorPicker,
  SquareColorPicker,
  ColorPickerPropsBase,
} from "components/ColorPicker/ColorPicker";
import { PaletteResponse } from "pages/media/types";
import {
  normalizeGradientForColorPicker,
  resolveColorToBrandKitPlaceholder,
  isGradient,
} from "../../helpers/itemsHelper";
import type { Color } from "components/ColorPicker/colorTypes";
import { GradientDefinition } from "types";
import { useCallback } from "react";
import { getDirectionFromGeometry } from "components/ColorPicker/types";

interface StyledSquareColorPickerProps {
  $backgroundColor?: string;
}

export interface PlaceholderColorPickerProps extends Omit<
  ColorPickerPropsBase,
  "onChangeComplete" | "value"
> {
  palette?: PaletteResponse;
  shape?: "default" | "round" | "square";
  triggerProps?: StyledSquareColorPickerProps;
  onChangeComplete: (color: string | GradientDefinition | undefined) => void;
  value?: string | GradientDefinition | null;
}

const PICKER_COMPONENTS = {
  default: ColorPicker,
  round: RoundColorPicker,
  square: SquareColorPicker,
} as const;

export const PlaceholderColorPicker = ({
  palette,
  shape = "default",
  triggerProps,
  onChangeComplete,
  value,
  ...colorPickerProps
}: PlaceholderColorPickerProps) => {
  const handleChangeComplete = useCallback(
    (color: Color, gradient?: GradientDefinition) => {
      if (gradient) {
        onChangeComplete(gradient);
        return;
      }
      const hexColor = color.toHexString();
      if (!palette) {
        onChangeComplete(hexColor);
        return;
      }
      onChangeComplete(resolveColorToBrandKitPlaceholder(hexColor, palette));
    },
    [onChangeComplete, palette]
  );

  const normalizedValue = value ?? undefined;

  const initialGradientDirection = isGradient(normalizedValue)
    ? getDirectionFromGeometry(normalizedValue.geometry)
    : undefined;

  const resolvedValue = normalizeGradientForColorPicker(
    normalizedValue,
    palette
  );

  const Picker = PICKER_COMPONENTS[shape];

  return (
    <Picker
      {...colorPickerProps}
      palette={palette}
      value={resolvedValue}
      triggerProps={triggerProps}
      onChangeComplete={handleChangeComplete}
      initialGradientDirection={initialGradientDirection}
    />
  );
};
